import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { InvoiceApi } from "../apis";
import { message } from "../utils/Message";
import { invoiceDescriptionForApi } from "./invoice-description-for-api";

type IFileItem = {
  name: string;
  description: string;
  url: string;
};

type Options = {
  isNew: boolean;
  id: string | undefined;
  userId: string | undefined;
};

export function useInvoiceSubmit({ isNew, id, userId }: Options) {
  const { t } = useTranslation("invoice");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const submit = async (values: any, send: boolean) => {
    setLoading(true);
    try {
      if (isNew) {
        await InvoiceApi.create({
          senderFinUserId: userId,
          senderBranchId: values.senderBranchId,
          senderAccId: values.senderAccId,
          receiverBusinessId: values.receiverBusinessId,
          paymentDate: values.paymentDate,
          receiverBranchId: values.receiverBranchId,
          description: invoiceDescriptionForApi(values?.description),
          discountType: values.discountType,
          discountValue: values.discountValue ? parseFloat(values.discountValue) : null,
          shippingAmount: values.shippingAmount ? parseFloat(values.shippingAmount) : null,
          attachments: (values?.attachments || [])?.map((item: IFileItem) => ({
            description: item.description,
            name: item.name,
            url: item.url,
          })),
          lines: values?.lines
            ?.filter((item: any) => !item?.additionalLines)
            ?.map((item: any) => ({
              variantId: item?.variantId || item?.id,
              discountType: item?.discountType,
              discountValue: item?.discountValue,
              quantity: item?.quantity || 0,
              warehouseId: item?.warehouseId || item?.inventories?.[0]?.warehouseId || "",
              parentVariantId: item?.parentVariantId || item?.variantParentId || item?.variantId || item?.id,
              price: item?.price,
            })),
          additionalLines: (values.additionalLines ?? []).map((item: any) => ({
            quantity: item?.quantity || 0,
            image: item?.image,
            name: item?.name,
            unit: item?.unit,
            discountType: item?.discountType,
            discountValue: item?.discountValue,
            price: item?.price || 0,
          })),
          send,
        });
      } else {
        await InvoiceApi.update(id || "", {
          senderFinUserId: userId,
          senderBranchId: values.senderBranchId,
          senderAccId: values.senderAccId,
          receiverBusinessId: values.receiverBusinessId,
          paymentDate: values.paymentDate,
          receiverBranchId: values.receiverBranchId,
          description: invoiceDescriptionForApi(values?.description),
          discountType: values.discountType,
          discountValue: values.discountValue ? parseFloat(values.discountValue) : null,
          shippingAmount: values.shippingAmount ? parseFloat(values.shippingAmount) : null,
          attachments: (values?.attachments || [])?.map((item: IFileItem) => ({
            description: item?.description,
            name: item?.name,
            url: item.url,
          })),
          lines:
            values?.lines?.map((item: any) => ({
              variantId: item?.variantId || item?.id,
              discountType: item.discountType,
              discountValue: item.discountValue,
              quantity: item.quantity || 0,
              warehouseId: item?.warehouseId || item?.inventories?.[0]?.warehouseId || "",
              parentVariantId: item?.parentVariantId || item?.variantParentId || item?.variantId || item?.id,
              price: item?.price,
            })) || [],
          additionalLines:
            values?.additionalLines?.map((item: any) => ({
              image: item?.image,
              name: item?.name,
              unit: item?.unit,
              price: item?.price,
              discountType: item?.discountType,
              discountValue: item?.discountValue || null,
              quantity: item?.quantity,
            })) || [],
          send,
        });
      }

      message.success(t("success.created"));
      navigate("/sales-invoice");
    } catch (error: any) {
      message.error(error.message || "");
    } finally {
      setLoading(false);
    }
  };

  return { submit, loading };
}
